import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { printLabels } from 'features/zebra/zebraUtils';
import { loadingActions } from 'store/slices/loadingSlice';
import { useAppDispatch } from 'store/store';
import toast from 'shared/utils/toast';
import { movementService } from '../api/movement.service';

const TRANSLATION_PREFIX = 'pages.movements';

export const useMovementSubmission = (dataGridRefreshRef: React.RefObject<any>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [selectedMovement, setSelectedMovement] = useState<any>(null);
  const [batchExpirationOptions, setBatchExpirationOptions] = useState<any[]>([]);
  const [isProductBatchDialogOpen, setIsProductBatchDialogOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState('');

  const validatePrintConfiguration = (movement) => {
    if (movement.print_labels && movement.print_barcodes) {
      toast.error(t(`${TRANSLATION_PREFIX}.print_error_labels_and_barcodes`));

      return false;
    }

    return true;
  };

  const submitMovement = async (values, expirationDate) => {
    dispatch(loadingActions.startLoading());

    try {
      const response = await movementService.moveProduct(values).then((res) => {
        dataGridRefreshRef.current?.();
        return res;
      });

      if ('warnings' in response && response.warnings.length > 0) {
        response.warnings.forEach((warning) => {
          if (warning?.batches) {
            toast.warning(
              t('alerts.earlier-expiration', {
                batches: warning.batches.join(', ')
              })
            );
            return;
          }

          toast.warning(warning.type);
        });
      }

      if (values.print_labels || values.print_barcodes) {
        printLabels(
          values.product,
          values.product_batch,
          expirationDate,
          values.moved_quantity,
          values.description,
          Boolean(values.print_barcodes)
        );
      }

      toast.success('Movimentazione avvenuta con successo');

      setSelectedBatchId('');
      setIsProductBatchDialogOpen(false);
    } catch (error) {
      const axiosError = error as AxiosError;

      const data = axiosError.response?.data as
        | {
            error?: string;
            detail?: string;
          }
        | undefined;

      const errorMessage =
        data?.error || data?.detail || axiosError.message || 'Movimentazione fallita';

      toast.error(errorMessage);
    } finally {
      dispatch(loadingActions.stopLoading());
    }
  };

  const handleMovementSubmit = async (values) => {
    const { movement } = values;

    if (!validatePrintConfiguration(movement)) {
      return;
    }

    await submitMovement(movement, movement.expiration_date || '');
  };

  const closeProductBatchDialog = () => {
    setIsProductBatchDialogOpen(false);
  };

  return {
    selectedMovement,
    batchExpirationOptions,
    isProductBatchDialogOpen,
    selectedBatchId,
    handleMovementSubmit,
    submitMovement,
    closeProductBatchDialog,
    setSelectedBatchId
  };
};
